import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import GRInput from '../../general-components/input/gr-input';
import StylePackRequestBtn from '../../style-packs/style-pack-request-btn';
import DraggingGrPicture from '../../general-components/input/dragging-gr-picture';
import GrPictureInput from '../../general-components/input/gr-picture-input';
import useDragContent from '../../../hooks/content/use-drag-content';
import { FormContext } from '../../../contexts/generated-content/content-form-context';

type FieldType = 'text' | 'number' | 'checkbox' | 'color' | 'select' | 'timer' | 'picture' | 'image' | 'photo' | 'string' | 'duration' | 'float' | 'int' | 'boolean';
type FieldDefault = string | number | boolean;

export interface Field {
	hasLabel?: boolean;
	name: string;
	type: FieldType;
	default: FieldDefault;
	validation: string | null;
	required: boolean;
	options: { name: string; value: string }[] | null;
	userData: string;
	variables: Array<any>;
}

export const InputTypeByCapsuleType: Record<FieldType, FieldType> = {
	image: 'picture',
	picture: 'picture',
	photo: 'picture',

	string: 'text',
	text: 'text',

	float: 'number',
	number: 'number',
	int: 'number',

	select: 'select',

	duration: 'timer',
	timer: 'timer',

	boolean: 'checkbox',
	checkbox: 'checkbox',

	color: 'color',
};

interface HeaderTypes {
	[key: string]: string;
}

const headerTypes:HeaderTypes = {
	image: 'Copy',
	string: 'Copy',
	duration: 'Copy',
	color: 'Style',
	float: 'Numbers',
	int: 'Numbers',
	boolean: 'Toggles',
	select: 'Style',
};

export const maxPictureCount = 3;

const StyledForm = styled.form`
	z-index: $z-index-media-link;
    padding: 0px 24px;
    position: static;
	flex: 1;
`;

const FormContainer = styled.div`
    display: flex;
	flex-direction: column;
	justify-content: space-between;
	height: calc(100% - 62px);
`;

const StyledDiv = styled.div`
	margin-top: 15px;
	& > h3.header-section {
		margin-top: 16px;
		margin-bottom: 10px;
		font-size: 14px;
		font-weight: 700;
	}
	.animation-row {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 10px;
		height: 40px;
		& > label {
			width: 100px;
			margin-right: 10px;
			color: #908ca0;
		}
	}
	& > hr.header-line {
		margin-top: 13px;
		margin-bottom: 13px;
		border-color: rgba(144,140,160,.1);
	}
`;

export default function ContentGeneratorHandler() {
	const {
		statefulFormValues,
		selectedGraphicApiShape,
		currTemplate,
		addNewPicture,
		removePictureWithCount,
		defaultTitleOverlayData,
		onDragEnd,
		onChange,
		uniqueId,
	} = useContext(FormContext);

	const {
		draggingCount,
		draggingOver,
		handleDragEnd,
		handleDragOver,
		handleDragStart,
		sensors,
	} = useDragContent(onDragEnd, currTemplate);

	const getGrPictureInput = (field: Field, count: number) => (
		<GrPictureInput
			key={`picture-${count}-${uniqueId}`}
			onChange={onChange}
			addPicture={addNewPicture}
			removePicture={removePictureWithCount}
			values={statefulFormValues}
			defaultValues={defaultTitleOverlayData?.default}
			count={count}
			totalCount={Number(statefulFormValues?.pictureCount ?? 1)}
			draggingCount={draggingCount}
			draggingOver={draggingOver}
			template={currTemplate}
			field={field}
		/>
	);

	const getDragOverlay = (field: Field) => (
		<DraggingGrPicture
			key={`picture-${field.userData}-${uniqueId}`}
			fieldName={field.userData}
			options={field.options}
			variables={field?.variables ?? []}
			values={statefulFormValues}
			defaultValues={defaultTitleOverlayData?.default}
			count={draggingCount}
		/>
	);

	const pictureInputComponent = (field: Field) => {
		const count = Number(statefulFormValues?.pictureCount ?? 1);
		const pictures = [];
		const names = [];
		for (let i = 0; i < count; i += 1) {
			pictures.push(getGrPictureInput(field, i + 1));
			names.push(`picture-${i}`);
		}
		return (
			<DndContext
				key={`${field.userData}-${uniqueId}`}
				sensors={sensors}
				onDragMove={handleDragOver}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
			>
				<SortableContext
					items={names}
					strategy={verticalListSortingStrategy}
				>
					{pictures}
				</SortableContext>
				{draggingCount > 0 && (
					<DragOverlay>
						{getDragOverlay(field)}
					</DragOverlay>
				)}
			</DndContext>
		);
	};

	const fieldsGroupedByType = useMemo(() => {
		const groups: { [type: string]: Field[] } = {};

		selectedGraphicApiShape.forEach((field) => {
			const { type: fieldType } = field;
			if (groups[headerTypes[fieldType]]) {
				groups[headerTypes[fieldType]].push(field);
			} else {
				groups[headerTypes[fieldType]] = [field];
			}
		});

		const groupedFields = Object.entries(groups);
		return groupedFields;
	}, [selectedGraphicApiShape]);

	const isWalkup = currTemplate.type === 'brandlive-walkups'
		|| (currTemplate.type === 'api-brandlive-1' && currTemplate.contentType === 'walkup');

	return (
		<FormContainer>
			<StyledForm
				autoComplete="off"
				className={'settings-container'}
				style={{ paddingBottom: currTemplate.locked ? '72px' : '0' }}
			>
				{fieldsGroupedByType.map(([type, fields], index) => (
					<StyledDiv key={`${type}-${uniqueId}`}>
						{type === 'Copy'
							? <h3 className='header-section'>
								{isWalkup ? 'Presenters' : 'Copy'}
							</h3>
							: <h3 className='header-section'>{type}</h3>
						}
						{type === 'Copy'
							&& currTemplate.type === 'brandlive-walkups'
							&& Number(statefulFormValues.pictureCount ?? 0) > 1
							&& (<>
								<GRInput
									key={`eventName-${currTemplate.type}`}
									fieldName="eventName"
									friendlyName="Event Name"
									fieldType={'text'}
									value={statefulFormValues.eventName ?? 'Brandlive Studios'}
									onChange={onChange}
									options={[]}
									customClassNameProp="gr-input-s1"
								/>
								<hr className='header-line' />
							</>)}
						{fields.map((f: Field) => (
							InputTypeByCapsuleType[f.type] === 'picture'
								? pictureInputComponent(f)
								: <GRInput
									key={`${f.userData}-${uniqueId}`}
									fieldName={f.userData}
									friendlyName={f.name}
									fieldType={InputTypeByCapsuleType[f.type]}
									onChange={onChange}
									options={f.options}
									value={statefulFormValues?.[f.userData]?.toString()}
									disabled={currTemplate.locked}
								/>
						))}
						{index !== fieldsGroupedByType.length - 1 && (
							<hr className='header-line' />
						)}
					</StyledDiv>
				))}
			</StyledForm>
			{!!currTemplate.locked && <StylePackRequestBtn stylePackName={currTemplate.name}/>}
		</FormContainer>
	);
}
